import { CsQuestion } from 'types/contentStack';
import {
  VALID_DATE_REGEX,
  VALID_EMAIL_ADDRESS_REGEX,
  VALID_PHONE_NUMBER_REGEX,
  VALID_POSTCODE_REGEX,
} from './consts';

export enum RegexRequiredFieldEnum {
  EMAIL = 'email',
  TELEPHONE = 'telephone',
  POSTCODE = 'postcode',
  DATE = 'date',
}

const regexRequiredTypes = ['email', 'telephone', 'postcode', 'date'] as const;
export type RegexRequiredFieldType = (typeof regexRequiredTypes)[number];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isRegexRequiredField = (value: any): value is RegexRequiredFieldType =>
  regexRequiredTypes.includes(value);

const checkRegex = (targetValue: string, regex: RegExp): boolean =>
  !!targetValue.match(regex);

export const validateDateTime = (field: CsQuestion, value: string): boolean => {
  const splitDate = value.split('/');
  const dateData = {
    day: parseInt(splitDate[0] ?? '', 10),
    month: parseInt(splitDate[1] ?? '', 10),
    year: parseInt(splitDate[2] ?? '', 10),
  };

  if (
    Number.isNaN(dateData.day) ||
    Number.isNaN(dateData.month) ||
    Number.isNaN(dateData.year)
  ) {
    return false;
  }

  if (dateData.month === 2) {
    const isLeapYear =
      dateData.year % 100 === 0 ? dateData.year % 400 === 0 : dateData.year % 4 === 0;
    const maxDay = isLeapYear ? 29 : 28;
    if (dateData.day < 1 || dateData.day > maxDay) {
      return false;
    }
  }

  const date = new Date(dateData.year, dateData.month - 1, dateData.day);
  const now = new Date();

  if (field.additional_validators?.date?.future_invalid) {
    return date.getTime() < now.getTime();
  }

  if (field.additional_validators?.date?.past_invalid) {
    return date.getTime() > now.getTime();
  }
  return true;
};

export const isValueValid = (value: string, field: CsQuestion): boolean => {
  const { EMAIL, POSTCODE, TELEPHONE, DATE } = RegexRequiredFieldEnum;
  const fieldType = field.type as RegexRequiredFieldType;
  switch (fieldType) {
    case EMAIL:
      return checkRegex(value, VALID_EMAIL_ADDRESS_REGEX);
    case POSTCODE:
      return checkRegex(value, VALID_POSTCODE_REGEX);
    case TELEPHONE:
      return checkRegex(value, VALID_PHONE_NUMBER_REGEX);
    case DATE:
      return checkRegex(value, VALID_DATE_REGEX) && validateDateTime(field, value);
    default:
      return true;
  }
};

export const isMandatoryField = (field: CsQuestion): boolean =>
  !!field.error_messages?.missing;

export const shouldVerifyIfValid = (field: CsQuestion): boolean =>
  !!field.error_messages?.invalid_value && isRegexRequiredField(field.type);

export const shouldAppearConditionally = (field: CsQuestion): boolean =>
  !!field.show_if_field &&
  !!field.show_if_field.value &&
  !!field.show_if_field.to_contain;
